import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Mail, MailOpen, Trash2, Inbox, UserPlus } from 'lucide-react';
import { contactAPI } from '../../services/api';
import { toast } from 'sonner';

const formatDate = (value) => new Date(value).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });

const ContactTab = ({ onUnreadChange }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try {
      const data = await contactAPI.getAll();
      setMessages(data);
      onUnreadChange?.(data.filter((m) => !m.read).length);
    } catch (e) {
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRead = async (msg) => {
    try {
      const res = await contactAPI.toggleRead(msg.contact_id);
      const updated = messages.map((m) => (m.contact_id === msg.contact_id ? { ...m, read: res.read } : m));
      setMessages(updated);
      onUnreadChange?.(updated.filter((m) => !m.read).length);
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const remove = async (msg) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await contactAPI.delete(msg.contact_id);
      const updated = messages.filter((m) => m.contact_id !== msg.contact_id);
      setMessages(updated);
      onUnreadChange?.(updated.filter((m) => !m.read).length);
      toast.success('Message supprimé');
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const open = (msg) => {
    setExpanded(expanded === msg.contact_id ? null : msg.contact_id);
    if (!msg.read) toggleRead(msg);
  };

  if (loading) {
    return <div className="text-purple-300 p-8 text-center">Chargement des messages...</div>;
  }

  return (
    <div className="space-y-4" data-testid="contact-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-purple-300 flex items-center gap-2">
          <Inbox className="h-5 w-5" /> Messages du formulaire de contact
        </h2>
        <span className="text-sm text-gray-400" data-testid="contact-count">{messages.length} message{messages.length > 1 ? 's' : ''}</span>
      </div>

      {messages.length === 0 && (
        <Card className="bg-black/40 border border-purple-500/20 p-10 text-center text-gray-400" data-testid="contact-empty">
          Aucun message reçu pour le moment.
        </Card>
      )}

      {messages.map((msg, index) => (
        <Card key={msg.contact_id} data-testid={`contact-message-${index}`}
          className={`border-2 p-4 sm:p-5 transition-colors ${msg.read ? 'bg-black/40 border-purple-500/20' : 'bg-purple-900/30 border-purple-400/50'}`}>
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
            <button type="button" onClick={() => open(msg)} className="text-left flex-1 min-w-0" data-testid={`contact-message-toggle-${index}`}>
              <div className="flex flex-wrap items-center gap-2">
                {msg.read ? <MailOpen className="h-4 w-4 text-gray-500" /> : <Mail className="h-4 w-4 text-purple-300" />}
                <span className={`font-bold break-words ${msg.read ? 'text-gray-300' : 'text-purple-100'}`}>{msg.subject}</span>
                {msg.wants_membership && (
                  <Badge className="bg-green-700/70 text-white border-green-400/40 text-xs"><UserPlus className="h-3 w-3 mr-1" /> Souhaite adhérer</Badge>
                )}
                {!msg.email_sent && <Badge className="bg-orange-700/70 text-white border-orange-400/40 text-xs">Email non envoyé</Badge>}
              </div>
              <p className="text-sm text-gray-400 mt-1 break-words">
                {msg.name} · <a href={`mailto:${msg.email}`} className="text-purple-400 hover:underline" onClick={(e) => e.stopPropagation()}>{msg.email}</a> · {formatDate(msg.created_at)}
              </p>
            </button>
            <div className="flex gap-2 flex-shrink-0">
              <Button size="sm" variant="outline" onClick={() => toggleRead(msg)} data-testid={`contact-toggle-read-${index}`}
                className="border-purple-500/40 text-purple-300 hover:bg-purple-900/40">
                {msg.read ? 'Marquer non lu' : 'Marquer lu'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => remove(msg)} data-testid={`contact-delete-${index}`}
                className="border-red-500/40 text-red-400 hover:bg-red-900/30">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {expanded === msg.contact_id && (
            <p className="mt-4 pt-4 border-t border-purple-500/20 text-gray-200 whitespace-pre-wrap break-words" data-testid={`contact-message-body-${index}`}>
              {msg.message}
            </p>
          )}
        </Card>
      ))}
    </div>
  );
};

export default ContactTab;
